import { lazy, Suspense, useRef } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { z } from "zod";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { matchRoute, renderRoute } from "./components/routing/RouteMatch";
import ArticlePage from "./pages/article/ArticlePage";
import WorkPage from "./pages/work/WorkPage";
import ArticlesListPage from "./pages/article/ArticlesListPage";
import ScreenshotAuthPage from "./pages/auth/ScreenshotAuthPage";
import {
  useCurrentUser,
  getCurrentUserData,
} from "./domains/user/hooks/useCurrentUser";
import { useEmailVisitEngagement } from "./domains/app/hooks/useEmailVisitEngagement";
import { useReportEmailClick } from "./domains/app/hooks/useReportEmailClick";
import { useRouterPageView } from "./domains/app/hooks/useRouterPageView";
import CompanyPage from "./view/company/CompanyPage";
import AuthorPage from "./view/author/AuthorPage";
import AuthorSearchPage from "./view/author-search/AuthorSearchPage";
import DiscoverySurface from "./view/discovery/DiscoverySurface";
import ForYouPage from "./view/forYou/ForYouPage";
import ChatWithPaperConversationPage from "./view/gpt/ChatWithPaperConversationPage";
import ChatWithPaperPage from "./view/gpt/ChatWithPaperPage";
import GPTConversationPage from "./view/gpt/GPTConversationPage";
import GPTSharedConversationPage from "./view/gpt/GPTSharedConversationPage";
import ActivationPairPage from "./view/gpt/ActivationPairPage";
import GPTNewChatPage from "./view/gpt/GPTNewChatPage";
import GPTAnonymousChatPage from "./view/gpt/GPTAnonymousChatPage";
import { useNewChatGeneration } from "./domains/gpt/stores/newChatGenerationStore";
import VisualAbstractReportPage from "./view/gpt/VisualAbstractReportPage";
import DesktopGPTHistoryPage from "./view/gpt/desktop/DesktopGPTHistoryPage";
import { ASSISTANT_DEBUG_FLAG } from "../../api/src/shared/growthbook-flags";
import VisualAbstractPreviewPage from "./features/visual-abstract/VisualAbstractPreviewPage";
import VisualAbstractLibraryPage from "./features/visual-abstract/VisualAbstractLibraryPage";
import VisualAbstractCreatePage from "./features/visual-abstract/VisualAbstractCreatePage";
import InfographicPage from "./view/infographic/InfographicPage";
import InviteLinkPage from "./view/invite/InviteLinkPage";
import AppInstall from "./view/app-install/AppInstall";
import { installWallFor } from "./domains/app/appInstallWall";
import InstallBottomSheet from "./view/app-install/InstallBottomSheet";
import JournalPage from "./view/journal/JournalPage";
import LoginPage from "./pages/auth/LoginPage";
import DesktopAuthPage from "./pages/auth/DesktopAuthPage";

import NotificationCenter from "./view/notifications/NotificationCenter";
import NotificationRouter from "./view/notifications/NotificationRouter";
import PlaylistPage from "./view/playlist/PlaylistPage";
import PlaylistsListPage from "./view/playlist/PlaylistsListPage";
import CreatePlaylist from "./view/profile/ProfileCreatePlaylist";
import ProfileJournals from "./view/profile/ProfileJournalPage";
import ProfilePage from "./view/profile/ProfilePage";
import { DesktopProfilePage } from "./view/profile/desktop/DesktopProfilePage";
import { DesktopProfileCollectionsPage } from "./view/profile/desktop/DesktopProfileCollectionsPage";
import { DesktopCollectionsForYouPage } from "./view/playlist/desktop/DesktopCollectionsForYouPage";
import ProfilePlaylistPage from "./view/profile/ProfilePlaylistPage";
import ProfilePlaylistsListPage from "./view/profile/ProfilePlaylistsListPage";
import ProfileBookmarkedPlaylistsListPage from "./view/profile/ProfileBookmarkedPlaylistsListPage";
import EditAvatarPage from "./view/profile/edit/EditAvatarPage";
import EditPreferredContentPage from "./view/profile/edit/EditPreferredContentPage";
import EditProfilePage from "./view/profile/edit/EditProfilePage";
import ProfileGamificationPage from "./view/profile/gamification/ProfileGamificationPage";
import RoomConferencesPage from "./view/room/RoomConferencesPage";
import AnnouncementHost from "./features/announcements/AnnouncementHost";
import AnnouncementPreviewPage from "./features/announcements/AnnouncementPreviewPage";
import RoomDiscoveryPage from "./view/room/RoomDiscoveryPage";
import RoomPodcastPage from "./view/room/RoomPodcastPage";
import RoomQuizListPage from "./view/room/RoomQuizListPage";
import RoomQuizSlidesPage from "./view/room/RoomQuizSlidesPage";
import RoomResourcesPage from "./view/room/RoomResourcesPage";
import CongressTopSessionsPage from "./view/room/CongressTopSessionsPage";
import CongressPdfPage from "./view/room/CongressPdfPage";
import { DesktopCongressSpacePage } from "./view/room/desktop/DesktopCongressSpacePage";
import RoomTermsPrivacyPage from "./view/room/RoomTermsPrivacyPage";
import { DesktopRoomDiscoveryPage } from "./view/room/desktop/DesktopRoomDiscoveryPage";
import { DesktopRoomResourcesPage } from "./view/room/desktop/DesktopRoomResourcesPage";
import RoomsCenterPage from "./view/rooms/RoomsCenterPage";
import { useRoomsLayout } from "./domains/room/hooks/useRoomsLayout";
import { SearchPage } from "./features/search/SearchPage";
import PolicyPage from "./view/settings/PolicyPage";
import RequestCertificationPage from "./view/settings/RequestCertificationPage";
import SettingsEditAccountPage from "./view/settings/SettingsEditAccountPage";
import SettingsEditLanguagePage from "./view/settings/SettingsEditLanguagePage";
import SettingsPage, {
  SettingsNotificationsPage,
  SettingsPartnerSharingPage,
  SettingsPartnersPage,
} from "./view/settings/SettingsPage";
import TermsPage from "./view/settings/TermsPage";
import { DesktopSettingsRoute } from "./view/settings/desktop/DesktopSettingsRoute";
import ConfirmEmailPage from "./view/signup/ConfirmEmailPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import { onboardingLocationStateSchema } from "./pages/onboarding/useOnboardingFlow";
import CountryFixupPage from "./pages/onboarding/CountryFixupPage";
import InterestsProfilingPage from "./pages/onboarding/InterestsProfilingPage";
import PracticeProfilingPage from "./pages/onboarding/PracticeProfilingPage";
import NotificationFixupPage from "./pages/onboarding/NotificationFixupPage";
import { useRoomWelcomeAtEntry } from "./components/room/RoomWelcomeHost";
import SignupQRCodePage from "./view/signup/SignupQRCodePage";
import ManageSpecialtiesPage from "./view/specialties/ManageSpecialtiesPage";
import Error404Page from "./view/static/Error404Page";
import VideoPage from "./view/video/VideoPage";
import VideoStoryPage from "./view/video/VideoStoryPage";
import WelcomePage from "./view/welcome/WelcomePage";
import {
  DefaultThemeProvider,
  RoomThemeProvider,
} from "./domains/app/contexts/theme.context";
import {
  ROOM_ROUTES,
  extractRoomIdFromPath,
} from "./domains/room/utils/roomIdFromPath";
import { getRoomColors } from "./domains/room/utils/getRoomColors";
import { getApiQueryData, useApiQuery } from "./services/api";
import { useRoomGuard } from "./domains/room/hooks/useRoomGuard";
import Loader from "./components/Loader";
import BootSkeleton from "./components/skeleton/BootSkeleton";
import { useDeferredDeepLink } from "./hooks/use-deferred-deep-link";
import { hasLoggedInStore } from "./domains/auth/stores/deferredDeepLinkStore";
import { getInitialRoute } from "./domains/app/utils/getInitialRoute";
import { campaignSearch } from "./tools/analytics/campaignParams";
import {
  getCachedBootDestination,
  useHomeSurface,
} from "./domains/app/hooks/useHomeSurface";
import {
  isDesktopShell,
  isDesktopLayout,
  isDesktopBrowserLayout,
} from "./common/hooks/useIsDesktopView";
import { DesktopAuthGateModal } from "./components/auth/DesktopAuthGateModal";
import { useDesktopAuthGateRequest } from "./domains/auth/hooks/useDesktopAuthGateRequest";
import { useAnonymousGptFrontDoor } from "./domains/gpt/anonymous/anonymousGpt";
import { useSkeletonTesting } from "./common/hooks/useSkeletonTesting";
import { isPublicBrowserPath } from "./domains/auth/publicBrowserPaths";
import { isRoomContentPath } from "./domains/room/utils/roomContentPath";
import { useI18nReady } from "./domains/app/hooks/useI18nReady";
import { memorizePostLoginRedirectPath } from "./domains/app/utils/postLoginRedirect";
import RoomPreviewCallbackPage from "./pages/oauth/RoomPreviewCallbackPage";
import { isUserOnboarded } from "./domains/user/utils/isUserOnboarded";
import { hasCompletedFeedProfiling } from "./domains/user/utils/hasCompletedFeedProfiling";
import { usePracticeArm } from "./domains/practice/hooks/usePracticeArm";
import { usePracticeProfilingGate } from "./domains/practice/hooks/usePracticeProfilingGate";
import { isScreenshotSession } from "./domains/app/screenshotSession";
import { useNotificationPermissionState } from "./domains/notification/hooks/useNotificationPermissionState";
import {
  NOTIFICATION_PROMPT_COOLDOWN_MS,
  notificationPromptDismissedAtStore,
} from "./domains/user/stores/pushTokensStore";
import { useStore } from "./lib/store";
import { useLocationState } from "./domains/app/hooks/useLocationState";
import {
  fixupNavigationStateSchema,
  fixupReturnState,
} from "./pages/onboarding/useFixupReturnTo";
import { auth } from "./services/firebase";
import { GPT_LANDING_PATH } from "@juisci/gpt-handoff";
import { useOnboardingFlowStore } from "./domains/auth/stores/onboardingFlowStore";
import { useOtpCredential } from "./domains/auth/stores/otpCredentialStore";
import { isAndroidApp } from "./tools/android";
import { isIOSApp } from "./tools/ios";
import { readPendingEmailCode } from "./domains/auth/hooks/useEmailOtp";

// Lazy, with json-render in LAZY_ONLY_PACKAGES (vite.config.ts): only the debug flag loads them.
const GenuiPlaygroundPage = lazy(
  () => import("./view/gpt/genui-playground/GenuiPlaygroundPage")
);

// Local-calendar-day comparison for the next-day push re-ask rule (UPP users
// who never had a push-permission opportunity are prompted when they reopen
// the app on a later day, never on the day they personalized).
function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Component that groups all room routes together.
 * This is wrapped by RoomThemeProvider.
 *
 * Gates the entire subtree on `roomQuery.state === "success"` so every
 * child receives a non-null, definitely-loaded `room: PrivateRoom`.
 * Exception: the consent onboarding path manages its own room fetch and
 * error UI, so it must render regardless of the outer query state.
 */
/**
 * Renders a shared piece of room content (`isRoomContentPath`) for a reader
 * who cannot load the room row: no session, or a room that refuses them.
 *
 * Reads the room from the PUBLIC endpoint, which has no visibility check and
 * hands back `toPublicRoomCard` — the room's identity and colours, never its
 * client profile or settings. That is the whole reason it can serve a private
 * room here without contradicting `guardRoomListing`: a card is not a shelf.
 */
function PublicRoomContentRoutes({ roomId }: { roomId: string }) {
  const roomQuery = useApiQuery("/webapp/public/rooms/:roomId", {
    param: { roomId },
    query: {},
  });
  if (roomQuery.state === "pending") return <Loader expanded />;
  // A card that does not load costs the THEME, not the article. The page below
  // needs a slug and an id; the card contributes colours. So a dead roomId in a
  // forwarded link, or a network blip, renders the share unthemed instead of
  // holding the reader on a spinner with no error state and no way out — which
  // is the failure this whole branch was added to end, and it would have been
  // reintroduced one level down.
  const primaryColor =
    roomQuery.state === "error" ? null : roomQuery.body.primaryColor;
  const { roomBackgroundColor, roomTextColor } = getRoomColors(primaryColor);
  return (
    <RoomThemeProvider
      backgroundColor={roomBackgroundColor}
      textColor={roomTextColor}
    >
      <Switch>
        {matchRoute(ROOM_ROUTES.article, ({ roomId, slug }) => (
          <DefaultThemeProvider>
            <ArticlePage roomId={roomId} slug={slug} />
          </DefaultThemeProvider>
        ))}
        {matchRoute(ROOM_ROUTES.playlist, ({ roomId, id }) => (
          <PlaylistPage roomId={roomId} id={id} />
        ))}
        {matchRoute(
          ROOM_ROUTES.video,
          ({ roomId, slug }) => (
            <VideoPage roomId={roomId} slug={slug} />
          ),
          { exact: true }
        )}
        {matchRoute(ROOM_ROUTES.videoStory, ({ roomId, slug }) => (
          <VideoStoryPage roomId={roomId} slug={slug} />
        ))}
        {matchRoute(ROOM_ROUTES.infographic, ({ roomId, slug }) => (
          <InfographicPage roomId={roomId} slug={slug} />
        ))}
        {matchRoute(
          ROOM_ROUTES.podcast,
          ({ roomId, podcastId }) => (
            <RoomPodcastPage roomId={roomId} podcastId={podcastId} />
          ),
          { exact: true }
        )}
      </Switch>
    </RoomThemeProvider>
  );
}

function RoomRoutes() {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useCurrentUser();
  // The room whose row this reader has been refused. Written during render, the
  // way `hasLoadedOnce` above it is, and for the same reason: it records
  // something that has already happened rather than driving anything.
  const refusedRoomRef = useRef<string | null>(null);
  const [newChatGeneration] = useNewChatGeneration();
  const roomId = extractRoomIdFromPath(location.pathname);
  const roomsLayout = useRoomsLayout();
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  useRoomGuard(roomId);
  useRoomWelcomeAtEntry(roomId);

  // roomId is null only for /room/<unknown> URLs: no room is named, so the
  // room query never runs and useRoomGuard never sees an error to redirect on.
  if (roomId === null) return <Error404Page />;

  // A shared piece of room content (`isRoomContentPath`: an article, a
  // playlist, a video, an infographic, a podcast) needs the room's APPEARANCE,
  // not its payload. `ArticlePage` takes `roomId` and `slug` and wraps itself in
  // `DefaultThemeProvider`; `PlaylistPage` takes `roomId` and `id`; the resource
  // pages read their content from the public API. None reads the room object.
  // They were behind this gate only because they sit under `/room/`, and the
  // gate waits on `/webapp/private/rooms/:id`, which runs `guardRoom` on
  // `isPublic` — so a logged-out reader who opened a link a colleague sent
  // waited on a loader that could never resolve.
  //
  // `room-access.ts` is explicit that this is backwards: a piece of room
  // content is PUBLIC and shareable without an account, whichever Room carries
  // it. The room ROW is what is gated, and these pages do not need it.
  //
  // MEMBERSHIP is not part of that rule either. A signed-in reader who is not in
  // a private client space cannot load its row any more than an anonymous one
  // can (`guardRoomListing` refuses them both; a public or discoverable room
  // serves its row to anyone signed in), and they used to be bounced to
  // `/discovery` by `useRoomGuard`'s 403 branch — so a colleague's link worked
  // for a stranger with no account and failed for a stranger with one.
  //
  // So the branch is taken for anyone who CANNOT have the row: no session, or a
  // session the room refuses. `roomQuery.state === "error"` is the second half,
  // and it has to be the settled error rather than "not a member", because
  // membership is not knowable before the request answers.
  //
  // Whoever gets the row keeps the private path unchanged: they get the rest
  // of the room subtree.
  // LATCHED, and it has to be. Read live, this answer oscillates: the branch it
  // selects mounts `ArticlePage`, which opens its OWN observer on this very
  // query, and a fresh observer refetches (`refetchOnMount`) — so `error` flips
  // back to `pending`, the branch flips back to the private one, `ArticlePage`
  // unmounts, and round it goes. Measured before this ref existed: 345 renders
  // in 2.5 seconds, every article request aborted by the next unmount, and the
  // reader looking at a spinner that never resolves.
  //
  // A refusal is also the kind of fact that does not need re-asking. `guardRoom`
  // answers from the room row and the reader's memberships; neither changes
  // under a reader who is standing on the page.
  //
  // A REFUSAL, not any error. A 403 is the room answering; a 500 or a dropped
  // connection is the network failing, and latching that would strand a member
  // on the public branch for the rest of the mount over a blip they would never
  // see again. An error that is not a refusal leaves the branch alone, which
  // also means no swap, which is what the oscillation needed.
  if (
    roomQuery.state === "error" &&
    roomQuery.status === 403 &&
    roomId !== null
  ) {
    refusedRoomRef.current = roomId;
  }
  const roomRowRefused = refusedRoomRef.current === roomId;
  const cannotLoadRoomRow = !isAuthenticated || (roomRowRefused && !isLoading);
  if (cannotLoadRoomRow && isRoomContentPath(location.pathname)) {
    return <PublicRoomContentRoutes roomId={roomId} />;
  }

  // A room page needs the room ROW, and a visitor with no session cannot have
  // it — `guardRoom` gates it on `isPublic`. Waiting for a request that cannot
  // succeed is how this subtree used to strand a reader on a spinner with no
  // timeout, no error state and no way out: Back out of a shared article and
  // the fallback lands on `/room/discovery/:roomId`, which then span forever.
  //
  // `useRoomGuard` already declines to ask when unauthenticated. This agrees
  // with it, and sends the reader where the app would have sent them anyway —
  // `/welcome`, which is the sign-up front door and the install wall.
  //
  // Keyed on the query having FAILED rather than on "not signed in", because
  // auth is briefly unknown on boot and a member landing straight on a room
  // page must not be bounced out of it while that resolves. A failed request
  // plus a settled signed-out session is a fact; `isAuthenticated` alone is
  // not, and it reads false for the whole of `isLoading`.
  //
  // MOBILE ONLY. A logged-out desktop visitor never gets here: `AppRoutes`
  // sends them to `/signup` first, or keeps the URL under the code modal while
  // the code they asked for is awaited.
  if (
    roomQuery.state === "error" &&
    !isAuthenticated &&
    !isLoading &&
    !isDesktopBrowserLayout()
  ) {
    memorizePostLoginRedirectPath();
    return <Redirect to="/welcome" />;
  }

  if (roomQuery.state !== "success") {
    return <Loader expanded />;
  }
  const room = roomQuery.body;
  const { roomBackgroundColor, roomTextColor } = getRoomColors(
    room.primaryColor
  );

  return (
    <RoomThemeProvider
      backgroundColor={roomBackgroundColor}
      textColor={roomTextColor}
    >
      <Switch>
        {/* NOTE: Room pages must always contain "/room/" and the roomId param. */}

        {matchRoute(ROOM_ROUTES.discovery, ({ roomId }) =>
          roomsLayout === "desktop" ? (
            <DesktopRoomDiscoveryPage room={room} />
          ) : (
            <RoomDiscoveryPage roomId={roomId} room={room} />
          )
        )}

        {/* The desktop room has no Congresses tab (Figma "Ready for dev",
            09/10, "04 - Rooms / Congress"): a congress is a Congress Space,
            a chip of the Resources tab and a banner in the room. The old
            tab's link lands on Resources, or on Discovery in a room without
            it. */}
        {matchRoute(ROOM_ROUTES.conferences, ({ roomId }) =>
          roomsLayout === "desktop" ? (
            <Redirect
              to={
                room.resourcesTab === true
                  ? `/room/resources/${roomId}`
                  : `/room/discovery/${roomId}`
              }
            />
          ) : (
            <RoomConferencesPage roomId={roomId} room={room} />
          )
        )}

        {matchRoute(ROOM_ROUTES.resources, ({ roomId }) =>
          roomsLayout === "desktop" ? (
            <DesktopRoomResourcesPage room={room} />
          ) : (
            <RoomResourcesPage roomId={roomId} room={room} />
          )
        )}

        {matchRoute(ROOM_ROUTES.congressTopSessions, ({ roomId, spaceId }) => (
          <CongressTopSessionsPage roomId={roomId} spaceId={spaceId} />
        ))}

        {matchRoute(ROOM_ROUTES.congressPdf, ({ roomId, spaceId, cardId }) => (
          <CongressPdfPage roomId={roomId} spaceId={spaceId} cardId={cardId} />
        ))}

        {/* After the two routes above: this pattern is their prefix. Desktop
            gives the space a page of its own; mobile keeps it as the Resources
            section it has always been, so a link works on both. */}
        {matchRoute(ROOM_ROUTES.congressSpace, ({ roomId, spaceId }) =>
          isDesktopLayout() ? (
            <DesktopCongressSpacePage
              roomId={roomId}
              spaceId={spaceId}
              room={room}
            />
          ) : (
            <Redirect
              to={(() => {
                // A desktop document link (`?document=<block id>`) lands on the
                // mobile PDF page; the space itself on its Resources section.
                const documentId = new URLSearchParams(location.search).get(
                  "document"
                );
                return documentId === null
                  ? `/room/resources/${roomId}?section=congress:${spaceId}`
                  : `/room/congress/${roomId}/${spaceId}/pdf/${documentId}`;
              })()}
            />
          )
        )}

        {matchRoute(ROOM_ROUTES.playlists, ({ roomId }) => (
          <PlaylistsListPage roomId={roomId} />
        ))}

        {matchRoute(
          ROOM_ROUTES.articleChat,
          ({ roomId, slug }) => (
            <ChatWithPaperPage roomId={roomId} slug={slug} />
          ),
          { exact: true }
        )}

        {matchRoute(
          ROOM_ROUTES.articleChatConversation,
          ({ roomId, slug, id }) => (
            <ChatWithPaperConversationPage
              roomId={roomId}
              slug={slug}
              id={id}
            />
          )
        )}

        {matchRoute(ROOM_ROUTES.article, ({ roomId, slug }) => (
          <DefaultThemeProvider>
            <ArticlePage roomId={roomId} slug={slug} />
          </DefaultThemeProvider>
        ))}

        {matchRoute(
          ROOM_ROUTES.video,
          ({ roomId, slug }) => (
            <VideoPage roomId={roomId} slug={slug} />
          ),
          { exact: true }
        )}

        {matchRoute(ROOM_ROUTES.videoStory, ({ roomId, slug }) => (
          <VideoStoryPage roomId={roomId} slug={slug} />
        ))}

        {matchRoute(ROOM_ROUTES.infographic, ({ roomId, slug }) => (
          <InfographicPage roomId={roomId} slug={slug} />
        ))}

        {matchRoute(ROOM_ROUTES.playlist, ({ roomId, id }) => (
          <PlaylistPage roomId={roomId} id={id} />
        ))}

        {matchRoute(
          ROOM_ROUTES.search,
          ({ roomId }) => (
            <SearchPage roomId={roomId} />
          ),
          { exact: true }
        )}

        {matchRoute(ROOM_ROUTES.gptChatNew, ({ roomId }) => (
          // Keyed on the sidebar's "New conversation" (see the store).
          <GPTNewChatPage key={newChatGeneration} roomId={roomId} />
        ))}

        {matchRoute(ROOM_ROUTES.gptChatConversation, ({ roomId, id }) => (
          <GPTConversationPage roomId={roomId} id={id} />
        ))}

        {matchRoute(ROOM_ROUTES.termsPrivacy, ({ roomId }) => (
          <RoomTermsPrivacyPage roomId={roomId} />
        ))}

        {matchRoute(
          ROOM_ROUTES.quiz,
          ({ roomId }) => (
            <RoomQuizListPage roomId={roomId} room={room} />
          ),
          { exact: true }
        )}

        {matchRoute(ROOM_ROUTES.quizSlides, ({ roomId, quizId }) => (
          <RoomQuizSlidesPage roomId={roomId} quizId={quizId} />
        ))}

        {matchRoute(
          ROOM_ROUTES.podcast,
          ({ roomId, podcastId }) => (
            <RoomPodcastPage roomId={roomId} podcastId={podcastId} />
          ),
          { exact: true }
        )}
      </Switch>
    </RoomThemeProvider>
  );
}

/**
 * The practice question, for a reader it can ask.
 *
 * Nothing to ask a signed-in reader with no Specialty, or whose Specialty has
 * no practices yet, nor a session whose /users/me, catalogue or saved answer
 * read failed (the API client broke). The page would render a paragraph or a
 * loader with no control, so the reader goes `home`. Keyed on the Firebase
 * session and never on the sign-in wall: the logged-out guard in `AppRoutes`
 * owns that reader.
 */
function PracticeQuestionRoute({ home }: { home: React.JSX.Element }) {
  const { currentUser, isLoading, isAuthenticated } = useCurrentUser();
  const specialtyUid =
    currentUser === null || currentUser.mainSpecialtyModel === null
      ? null
      : currentUser.mainSpecialtyModel.uid;
  // The editor's key, so the page issues no second request. Trusted only once
  // settled: a cached empty body being refetched may be a catalogue that
  // shipped since.
  const offerQuery = useApiQuery(
    "/webapp/public/practice-profiles/offered",
    specialtyUid === null ? false : { query: { specialtyUid } }
  );
  // `state: "error"` hides the cache (`createQueryResult` checks the error
  // first), and a focus refetch that fails mid-question is not a broken API for
  // a reader who has their catalogue. The cache is the last answer, as in
  // `useOnboardingFlow`; only an error with nothing cached sends them home.
  const offer =
    offerQuery.state === "success"
      ? offerQuery.body
      : offerQuery.state === "error" && specialtyUid !== null
        ? getApiQueryData("/webapp/public/practice-profiles/offered", {
            query: { specialtyUid },
          })
        : null;
  // The saved answer, the editor's other read, by the same rule.
  const heldQuery = useApiQuery(
    "/webapp/private/practice-profiles/me",
    specialtyUid === null ? false : {}
  );
  const heldFailed =
    !heldQuery.isFetching &&
    heldQuery.state === "error" &&
    getApiQueryData("/webapp/private/practice-profiles/me", {}) === null;
  const cannotAsk =
    specialtyUid === null ||
    heldFailed ||
    (!offerQuery.isFetching &&
      (offer === null
        ? offerQuery.state === "error"
        : offer.docs.length === 0));
  if (isAuthenticated && !isLoading && cannotAsk) return home;
  return <PracticeProfilingPage />;
}

function SignupOnboardingRoute({
  isLoading,
  isAuthenticated,
}: {
  isLoading: boolean;
  isAuthenticated: boolean;
}) {
  const location = useLocation();
  const { email: stateEmail } = useLocationState(onboardingLocationStateSchema);
  const [flow] = useOnboardingFlowStore();
  const [credential] = useOtpCredential();

  // The code's proof, held in memory, is a signup this tab is walking. It comes
  // BEFORE the loader: the name slide spends the proof to sign in, and Firebase
  // reports that session while `/users/me` is still in flight. Answering that
  // round trip with the loader unmounted the carousel and mounted it again, a
  // blank screen mid-signup that reads as a reload. A boot never holds a proof
  // (a reload drops it), so every boot still waits behind the loader.
  //
  // The desktop gate holds the verified address the same way. A reload loses
  // it, so in a desktop browser the signup door must collect and verify the
  // address again. A native shell, also on a tablet with the desktop layout,
  // verifies on the onboarding's own code slide, as a phone does: that is how
  // a reader comes back to a code mailed before the OS killed the app.
  if (credential !== null) return <OnboardingPage />;
  if (isLoading) return <Loader expanded />;
  if (isAuthenticated) return <OnboardingPage />;
  // The signup sheet hands over the address it just checked, on every device:
  // desktop visitors land on `/welcome` too, and the carousel opens on the code
  // slide. Refusing it on desktop sent the sheet's own submit back to `/signup`,
  // which reopened the sheet with an empty field.
  if (stateEmail !== null && z.email().safeParse(stateEmail).success)
    return <OnboardingPage />;
  if (
    !isDesktopBrowserLayout() &&
    !flow.accountCreated &&
    z.email().safeParse(flow.email).success
  ) {
    return <OnboardingPage />;
  }

  // A cold link has no mailbox for the code slide. `/signup` collects one: the
  // signup door on a desktop browser, the `/welcome` signup sheet elsewhere.
  return (
    <Redirect
      to={{ pathname: "/signup", search: campaignSearch(location.search) }}
    />
  );
}

export default function AppRoutes() {
  const navigationState = useLocationState(fixupNavigationStateSchema);
  useRouterPageView();
  useReportEmailClick();
  useEmailVisitEngagement();
  useDeferredDeepLink();

  const { currentUser, isLoading, isAuthenticated } = useCurrentUser();
  const desktopGateRequest = useDesktopAuthGateRequest();
  const anonymousGptFrontDoor = useAnonymousGptFrontDoor();
  const isAssistantDebug = useFeatureIsOn(ASSISTANT_DEBUG_FLAG);
  const location = useLocation();
  const [newChatGeneration] = useNewChatGeneration();
  // The practice arm does not derive its feed from topics, so the topics
  // profiling gate below must not stand in front of it. Read here, reactively,
  // and consulted only by that gate.
  const practiceArm = usePracticeArm();
  const practiceProfilingGate = usePracticeProfilingGate();
  const skeletonTesting = useSkeletonTesting();
  const { permissionState } = useNotificationPermissionState();
  const [notifDismissedAt] = useStore(notificationPromptDismissedAtStore);
  // Where this user lives. Read reactively, and `pending` until the GrowthBook
  // payload and this user's attributes have both landed — the two redirects
  // below hold rather than guess, since neither can be taken back.
  const homeSurface = useHomeSurface();
  const hasLoadedOnce = useRef(false);
  // Whether this load has shown anything past the entry (`/`, `/welcome`).
  // Read and set by the mailed-code redirect below.
  const hasLeftEntry = useRef(false);
  if (currentUser !== null || !isLoading) hasLoadedOnce.current = true;

  const i18nReady = useI18nReady();

  // Firebase boot gate: block while a returning user's persisted token is
  // validated server-side. Fresh visitors skip the skeleton (they resolve to
  // /welcome in a tick).
  const isFirebaseBoot =
    !hasLoadedOnce.current &&
    isLoading &&
    !currentUser &&
    location.pathname !== "/welcome";

  // i18n gate: block ALL routes until translations are loaded.
  // Without this, t() returns the raw key (parseMissingKeyHandler) — the user
  // sees "Supported by {{organisationName}}" instead of the translated text.
  const needsI18nGate = !i18nReady;

  // The silhouette a root boot should paint, from the persisted user cache
  // (available before the Firebase round-trip resolves, unlike `currentUser`)
  // plus this device's last settled destination — a pinned room alone no longer
  // implies the room, since a per-room rule can send its members to JuisciGPT.
  const cachedUser = getCurrentUserData();
  const cachedBootDestination = getCachedBootDestination(
    cachedUser === null ? null : cachedUser.pinnedRoom
  );

  if (skeletonTesting || isFirebaseBoot || needsI18nGate) {
    // `?skeleton` forces the shell regardless of auth. Otherwise: returning
    // users carry a persisted Firebase token whose server-side validation (the
    // `accounts:lookup` round-trip) is what makes cold boot slow; paint the
    // Discovery shell skeleton instead of a blank screen while it resolves.
    // Fresh users have no token — auth resolves in a tick straight to /welcome —
    // so a skeleton would only flash the wrong destination.
    // The i18n gate applies to everyone: a fresh visitor on a content route
    // should see a skeleton, not raw translation keys.
    //
    // Read the pinned room from the persisted user cache (available before the
    // Firebase round-trip resolves, unlike `currentUser`) so a root boot that
    // will redirect to the pinned room (getInitialRoute) shows that room's
    // skeleton instead of the main feed's.
    if (!skeletonTesting && !hasLoggedInStore.get() && !needsI18nGate) {
      // "auth resolves in a tick" is not "auth resolves now", and `null` is a
      // blank page for the length of that tick — 95 to 145ms of empty document
      // across local boots, and the Firebase round-trip it waits on is the
      // slowest thing on a cold mobile connection.
      //
      // So this visitor gets a skeleton too. What the original comment got
      // right is that theirs is not the RETURNING user's skeleton: they have no
      // cached user and no last destination, so `cachedBootDestination` would
      // paint a home they have never had. On every path but the root that
      // objection does not apply at all — `BootSkeleton` dispatches on the
      // pathname, and a fresh visitor deep-linking to a shared paper wants the
      // article silhouette exactly as much as a member does. At the root it is
      // answered by naming where they are actually going: the front door.
      //
      // Deliberately NOT a redirect. Taking `getInitialRoute` early would have
      // to guess the auth answer, and `hasLoggedIn` is precisely the wrong
      // proxy for it: a member whose storage was cleared (ITP eviction, a wiped
      // profile) reads as fresh, and would be parked on the front door instead
      // of their home. A skeleton costs nothing if the guess would have been
      // wrong.
      return <BootSkeleton rootDestination="front-door" />;
    }
    return <BootSkeleton rootDestination={cachedBootDestination} />;
  }

  // A held site question belongs to its original account through the final send.
  if (
    navigationState !== null &&
    navigationState.initialQuestionOwnerUid !== null &&
    (auth.currentUser === null ||
      auth.currentUser.uid !== navigationState.initialQuestionOwnerUid)
  )
    return <Redirect to={GPT_LANDING_PATH} />;

  // Push logged-out users to the login wall instead of letting them browse in
  // an anonymous "ghost" mode. EVERY logged-out visitor on a private path is
  // redirected, on desktop and on mobile alike.
  //
  // Mobile used to exempt the reader who had never signed in on this install
  // (`hasLoggedInStore`), on the reasoning that a fresh visitor should keep the
  // anonymous surfaces. That reasoning has moved: the anonymous surfaces are
  // now named explicitly in `PUBLIC_BROWSER_PATHS` — the auth and legal pages,
  // a shared paper, a shared playlist, a shared conversation — and everything
  // outside that list is the app, which asks you to install or sign in. Under
  // the old rule a brand-new visitor could browse `/discovery`, `/saved` and
  // `/search` and never be asked anything, while a returning one was stopped at
  // the door: the reader who knew Juisci least was asked least.
  //
  // Desktop was already unconditional, and for a reason that still holds: it
  // has no logged-out shell, and a logged-out user on a private route fires
  // zero requests — every query is gated on Firebase auth — so there is no 401
  // to trigger the session-expired redirect and the page would simply stay
  // blank.
  //
  // "Logged out" is keyed on the FIREBASE session (`isAuthenticated`), never
  // on `currentUser === null`: a failed /users/me (API down) also yields null,
  // and bouncing an authenticated user to the login wall here is what looped
  // /welcome ↔ /discovery (LoginPage reads the Firebase token and sends them
  // straight back). A 401 does not sign the webapp out either (`services/api.ts`
  // `onUnauthorized`): the session ends when a Firebase token refresh proves
  // the credential invalid (`useFirebaseUser`), which this guard then follows.
  //
  // The root path "/" is the app entry, not a content route. Mobile web walls
  // it above; the native shell falls through to the getInitialRoute redirect
  // below (→ /welcome, the front door with its sign-in CTAs), on desktop too.
  const isLoggedOutOnAPrivatePath =
    !isAuthenticated &&
    !isLoading &&
    !isPublicBrowserPath(location.pathname) &&
    location.pathname !== "/";
  // Desktop sends the visitor to `/signup` instead, with one exception: the
  // code the door has just asked for. While it is awaited, the route renders
  // and the auth gate stands over it, so the visitor sees the product they are
  // signing into. The gate is mounted at the end of this component, beside the
  // route tree, and `AppLayout` makes the shell inert from the same hook
  // (`useDesktopAuthGateRequest`), so the two cannot disagree. On
  // `/gpt/chat/new` the logged-out JuisciGPT page stands in place of both.
  // The install wall. On mobile web it is HARD on every page of the app, the
  // login and the signup included, and it stands on the URL that was asked for
  // so the store hop records that page. Shared content gets a door instead,
  // because the API serves it with no session and walling it out would break
  // a link a colleague sent. Decided in one pure function so the rule is
  // readable and testable: `domains/app/appInstallWall`.
  const installWall = installWallFor({
    pathname: location.pathname,
    search: location.search,
    isAuthenticated,
    isLoading,
    isNativeApp: isIOSApp() || isAndroidApp(),
    isDesktop: isDesktopLayout(),
  });
  // A question the website handed over is answered before the install ask,
  // which then follows the answer on the page (`useAnonymousGptFrontDoor`).
  if (installWall.kind === "hard" && anonymousGptFrontDoor.kind === "closed") {
    return <AppInstall escape={{ kind: "none" }} />;
  }

  if (
    isLoggedOutOnAPrivatePath &&
    !isDesktopBrowserLayout() &&
    anonymousGptFrontDoor.kind === "closed"
  ) {
    // The native shell, or mobile web under an `?appInstall`/demo bypass: the
    // install wall stepped aside above. Remember the path they were bounced
    // off so the login or the signup restores it
    // (`resolvePostAuthDestination`), and send them to the front door. After a
    // fresh install, the deferred deep link lands here with the page the
    // reader asked for on mobile web.
    memorizePostLoginRedirectPath();
    return <Redirect to="/welcome" />;
  }
  if (
    isLoggedOutOnAPrivatePath &&
    desktopGateRequest === null &&
    anonymousGptFrontDoor.kind === "closed"
  ) {
    // The desktop front door is the signup page. The page they were bounced
    // off is memorized for the same reason as above, and it is also the app
    // the code modal shows behind it.
    memorizePostLoginRedirectPath();
    return <Redirect to="/signup" />;
  }

  // The reader left the app to read the code we mailed, and the OS reloaded or
  // killed it meanwhile (on Android, the renderer or the process of a
  // backgrounded app): the boot lands here with nothing in memory. Send them
  // back to the screen waiting for that code, which resumes it without mailing
  // another. Only while this load has seen nothing past the entry, so Back out
  // of that screen onto the front door stays there. Not `history.length`: the
  // native shell also reloads the entry inside a WebView that keeps its history.
  // Set past the logged-out guard, so a private path it bounces to `/welcome`
  // (a link opened again on a killed app) does not count as shown.
  if (location.pathname !== "/" && location.pathname !== "/welcome") {
    hasLeftEntry.current = true;
  }
  const pendingCode =
    location.pathname === "/welcome" &&
    !hasLeftEntry.current &&
    !isAuthenticated &&
    !isLoading &&
    // A desktop browser keeps its tab. A native shell can be killed while
    // the reader is in the mail app, also on a tablet with the desktop layout.
    !isDesktopBrowserLayout()
      ? readPendingEmailCode()
      : null;
  if (pendingCode !== null) {
    switch (pendingCode.intent) {
      case "signup":
        return (
          <Redirect
            to={{
              pathname: "/signup/onboarding",
              state: { email: pendingCode.email },
            }}
          />
        );
      case "login":
        return (
          <Redirect
            to={{ pathname: "/login", state: { email: pendingCode.email } }}
          />
        );
      default:
        throw new Error(pendingCode.intent satisfies never);
    }
  }

  // Symmetric to the logged-out guard: a logged-in user has nothing to do on
  // the logged-out front door. Both /welcome and /login are routinely reached
  // *before* auth resolves — boot lands on /welcome whenever the "has logged in
  // on this install" flag is absent (cleared storage, ITP eviction, fresh
  // webview) even though the Firebase session is still valid, and the
  // logged-out guard above bounces there on any frame where currentUser is
  // momentarily null. Nothing re-evaluated once the user resolved, so the user
  // stayed parked on the welcome screen. The router owns this transition — no
  // page may check on mount whether it should exist.
  const isLoggedOutOnlyPath =
    location.pathname === "/welcome" ||
    location.pathname === "/login" ||
    location.pathname === "/signup";

  // The home redirect, shared by the two entries that take it: the root path and
  // a signed-in user parked on the logged-out front door.
  //
  // `pending` paints the boot skeleton instead of redirecting. This is the one
  // place the GrowthBook race actually bites — it fires at mount, on every boot,
  // and a redirect taken on an unloaded payload is never taken back
  // (`.knowledge/growthbook-oneshot-flag-race.md`). The wait overlaps the
  // /users/me round-trip that already gates this boot, and it stands in front of
  // these two entries only, so no deep link pays for it.
  const homeRedirect =
    homeSurface.kind === "pending" ? (
      <BootSkeleton rootDestination={cachedBootDestination} />
    ) : (
      <Redirect
        to={getInitialRoute({
          currentUser,
          isAuthenticated,
          homeDestination: homeSurface,
        })}
      />
    );

  if (currentUser !== null && isLoggedOutOnlyPath) {
    // Mirror the post-login destination logic (services/firebase
    // `loginAndRedirect`): an authenticated but incomplete profile belongs in
    // onboarding, everyone else in their home surface.
    if (!isUserOnboarded(currentUser)) {
      return <Redirect to="/signup/onboarding" />;
    }
    return homeRedirect;
  }

  if (location.pathname === "/") {
    return homeRedirect;
  }

  // A signed-in user whose onboarding is not finished: the account exists from
  // the name slide, but the profile is saved only by the last Continue (the
  // partner objection slide when its flag is on). A reader who leaves the flow
  // there must not use the app with no profession: the onboarding resumes on
  // the slide they left. Public paths stay open, as before: a shared
  // content page and the invite and OAuth entries run their own flow.
  const needsOnboarding =
    currentUser !== null &&
    !isUserOnboarded(currentUser) &&
    !isScreenshotSession();
  if (
    needsOnboarding &&
    !location.pathname.startsWith("/onboarding") &&
    !isPublicBrowserPath(location.pathname)
  ) {
    return (
      <Redirect
        to={{ pathname: "/signup/onboarding", search: location.search }}
      />
    );
  }

  // Onboarded users whose country was never persisted (SSO signup bug):
  // force them through a one-screen country picker before any other route.
  // Screenshot sessions must render the requested route verbatim — never
  // detour through an onboarding fixup interstitial.
  const needsCountryFixup =
    currentUser !== null &&
    isUserOnboarded(currentUser) &&
    !isScreenshotSession() &&
    (currentUser.country === null || currentUser.country === "");
  if (
    needsCountryFixup &&
    location.pathname !== "/onboarding/country" &&
    !location.pathname.startsWith("/signup") &&
    location.pathname !== "/login" &&
    location.pathname !== "/welcome"
  ) {
    return (
      <Redirect
        to={{
          pathname: "/onboarding/country",
          search: `?returnTo=${encodeURIComponent(location.pathname + location.search)}`,
          state: fixupReturnState(
            currentUser.uid,
            navigationState,
            location.pathname
          ),
        }}
      />
    );
  }

  // The full-page notification interstitial, now reduced to the ONE case only a
  // full page can serve: a NATIVE user who denied at the OS level, whom we can
  // send to system settings.
  //
  // `notDetermined` is deliberately NOT handled here any more. The in-app asks
  // own it — `PushOptInModal` on Explore and on profiling completion, and the
  // JuisciGPT `PushSuggestionCard`. This guard runs at the ROUTER level, so
  // leaving `notDetermined` in would redirect the user to the interstitial
  // before Explore ever rendered its modal: the two would compete for the same
  // population and the redirect would always win.
  //
  // Web-denied stays excluded: browsers don't re-prompt after an explicit
  // denial and we have no deep-link to per-site permission settings, so
  // redirecting would only render a "Turn on Notifications" CTA that no-ops.
  // Native keeps the cooldown below — a reinstall resets the OS permission but
  // not the user's account, so re-asking after a while is legitimate there, and
  // the localStorage flag is wiped on uninstall so a fresh install asks again.
  //
  // `preferenceOnboarding` may be undefined at runtime: the current user is
  // restored from a persisted react-query cache that can predate the field.
  const isNative = isIOSApp() || isAndroidApp();
  const permissionNeedsFixup =
    permissionState !== null && permissionState.status === "denied" && isNative;
  const preferenceOnboarding =
    currentUser === null ? undefined : currentUser.preferenceOnboarding;
  const uppCompletedAt =
    preferenceOnboarding !== undefined &&
    preferenceOnboarding.status === "completed"
      ? preferenceOnboarding.completedAt
      : null;
  const mayPromptForNotifications =
    notifDismissedAt === null
      ? uppCompletedAt !== null &&
        !isSameLocalDay(new Date(uppCompletedAt), new Date())
      : isNative &&
        Date.now() - notifDismissedAt > NOTIFICATION_PROMPT_COOLDOWN_MS;
  const needsNotificationFixup =
    currentUser !== null &&
    isUserOnboarded(currentUser) &&
    !needsCountryFixup &&
    !isScreenshotSession() &&
    permissionNeedsFixup &&
    mayPromptForNotifications;
  if (
    needsNotificationFixup &&
    location.pathname !== "/onboarding/notifications" &&
    !location.pathname.startsWith("/signup") &&
    !location.pathname.startsWith("/onboarding") &&
    location.pathname !== "/login" &&
    location.pathname !== "/welcome"
  ) {
    return (
      <Redirect
        to={{
          pathname: "/onboarding/notifications",
          search: `?returnTo=${encodeURIComponent(location.pathname + location.search)}`,
          state: fixupReturnState(
            currentUser.uid,
            navigationState,
            location.pathname
          ),
        }}
      />
    );
  }

  // The Explore gate. JuisciGPT is the home now, and `/discovery` is billed as
  // "Explore" — a personalized feed. Nobody reaches it without having walked the
  // profiling conversation: Explore and ALL its sub-surfaces route into
  // `/onboarding/interests` until `hasCompletedFeedProfiling`. Everything else
  // (JuisciGPT, Rooms, a shared article deep-link) stays reachable, so this is a
  // feed restriction, not a wall in front of the app.
  //
  // It applies to room members too — asking for Explore IS asking for the
  // personalized B2C feed, whoever you are. What must never happen is arriving
  // here without having asked: a room join used to land on `/discovery` to show
  // the `RoomJoinedModal`, so an invited member met this gate having only
  // accepted an invitation. Joins now land in the room itself (`joinRoom`,
  // `InviteLinkPage`), and the page carries a close button — the gate is a
  // detour on a surface the user chose, never a room they cannot leave. The
  // practice gate below keeps its cross too.
  //
  // The gate reads the conversation's own completion stamp, NOT whether the user
  // has interests: JuisciGPT's suggestion card and the plain topic picker both
  // write interests, so an interests-keyed gate would open Explore for a user
  // who never saw the conversation. Someone who has already chatted with
  // JuisciGPT still walks it (their existing interests arrive pre-selected, so
  // it is a short walk, not a re-do).
  //
  // `openPreferences=1` (once the destination of a profile push) used to
  // open a bottom sheet on Discovery; that sheet is gone, so the deep-link now
  // lands on the same page — including for an already-completed user coming back
  // to re-personalize (hence the check on the flag, not on completion).
  //
  // The practice arm (a Specialty with a catalogue, `usePracticeArm`) does not
  // lift the gate, it SUBSTITUTES one. The conversation exists to collect topics, and topics are what that arm's feed
  // no longer reads — holding a practitioner in front of it would make them
  // answer a question the surface behind it ignores. What replaces it, a few
  // lines down, is the same detour asking the question that arm's feed DOES
  // read. Same place in the funnel, different question: an arm with no gate at
  // all would be compared against one everybody walks, and its confirmation
  // rate would be measuring a different population.
  //
  // An UNSETTLED arm also does not gate, and that is the careful half. This is
  // a REDIRECT: decided before the catalogue read lands, it sends a practice-arm practitioner
  // into a topics conversation they can only leave by finishing it, and no later
  // re-render takes that back. Deferring costs the control arm a few frames of
  // Explore before the redirect fires — the gate is a funnel, not a boundary, and
  // that is the cheaper of the two errors.
  const wantsPreferenceProfiling =
    new URLSearchParams(location.search).get("openPreferences") === "1";
  // `out` and not `!== "in"`: while the arm is still `pending` nobody is
  // redirected, which is the behaviour this had before the union lost its
  // second arm.
  const topicsProfilingApplies = practiceArm === "out";
  const needsInterestsProfiling =
    topicsProfilingApplies &&
    currentUser !== null &&
    isUserOnboarded(currentUser) &&
    !isScreenshotSession() &&
    (wantsPreferenceProfiling || !hasCompletedFeedProfiling(currentUser));
  if (needsInterestsProfiling && location.pathname.startsWith("/discovery")) {
    return <Redirect to="/onboarding/interests" />;
  }

  // The practice arm's half of the same gate. `usePracticeProfilingGate` holds
  // the whole rule, including the deferral above: it answers `needed` only for a
  // settled arm, a settled pair of reads and a Specialty with practices to
  // offer, so an unsettled anything renders Explore rather than a screen the
  // practitioner could only leave by answering it.
  if (
    practiceProfilingGate === "needed" &&
    !isScreenshotSession() &&
    location.pathname.startsWith("/discovery")
  ) {
    return <Redirect to="/onboarding/practice" />;
  }

  return (
    <>
      {/* The code half of the desktop door. Rendered BESIDE the routes, not
          instead of them: the point is that the app is visible behind it. */}
      {desktopGateRequest !== null && (
        <DesktopAuthGateModal request={desktopGateRequest} />
      )}
      {/* The install ask for a logged-out mobile reader who followed a shared
          link. Beside the routes for the same reason as the gate above: they
          came for the article, so the article renders and the ask sits on top
          of it. Dismissal dies with the document, so it returns on a reload and
          stays away while they walk a playlist. */}
      {installWall.kind === "sheet" && <InstallBottomSheet />}
      {/* Pop-up announcements, for the same reason and in the same place. One
          mount reaches the reader wherever they landed, which for a lot of them
          is Juisci GPT or a room rather than the feed. The host holds the list
          of flows it must not interrupt. */}
      <AnnouncementHost />
      <Switch>
        {renderRoute("/onboarding/interests", () => (
          <InterestsProfilingPage />
        ))}
        {renderRoute("/onboarding/practice", () => (
          <PracticeQuestionRoute home={homeRedirect} />
        ))}
        {renderRoute("/onboarding/country", () => (
          <CountryFixupPage />
        ))}
        {renderRoute("/onboarding/notifications", () => (
          <NotificationFixupPage />
        ))}
        {renderRoute("/screenshot-auth", () => (
          <ScreenshotAuthPage />
        ))}
        {/* The admin visualizer frames this. Public (publicBrowserPaths) and
            standalone: one announcement, no feed behind it, no session. */}
        {matchRoute("/announcements/preview/:slug", ({ slug }) => (
          <AnnouncementPreviewPage slug={slug} />
        ))}
        {/* Desktop has no welcome page: its front door is the signup page. */}
        {renderRoute("/welcome", () =>
          isDesktopBrowserLayout() ? (
            <Redirect
              to={{
                pathname: "/signup",
                search: campaignSearch(location.search),
              }}
            />
          ) : (
            <WelcomePage />
          )
        )}

        {renderRoute(
          "/discovery",
          () => (
            <DiscoverySurface />
          ),
          { exact: true }
        )}
        {renderRoute("/discovery/author-search", () => (
          <AuthorSearchPage />
        ))}
        {renderRoute("/specialties/manage", () => (
          <ManageSpecialtiesPage />
        ))}
        {/* The video lists are gone. Their addresses live on in emails, push
            payloads and bookmarks, so they land on Discovery. */}
        {renderRoute("/discovery/videos", () => (
          <Redirect to="/discovery" />
        ))}
        {renderRoute(
          "/discovery/publications",
          () => (
            <ArticlesListPage />
          ),
          {
            exact: true,
          }
        )}
        {matchRoute("/discovery/publications/:slug", ({ slug }) => (
          <ArticlesListPage slug={slug} />
        ))}
        {renderRoute("/discovery/playlists", () =>
          isDesktopLayout() ? (
            <DesktopCollectionsForYouPage />
          ) : (
            <PlaylistsListPage roomId={null} />
          )
        )}
        {renderRoute(
          "/foryou",
          () => (
            <ForYouPage />
          ),
          { exact: true }
        )}

        {matchRoute(
          "/video/:slug",
          ({ slug }) => (
            <VideoPage slug={slug} roomId={null} />
          ),
          { exact: true }
        )}
        {renderRoute("/videos/stories", () => (
          <Redirect to="/discovery" />
        ))}
        {matchRoute("/video/story/:slug", ({ slug }) => (
          <VideoStoryPage roomId={null} slug={slug} />
        ))}

        {matchRoute(
          "/post/:slug/chat",
          ({ slug }) => (
            <ChatWithPaperPage roomId={null} slug={slug} />
          ),
          { exact: true }
        )}
        {matchRoute("/post/:slug/chat/conversation/:id", ({ slug, id }) => (
          <ChatWithPaperConversationPage roomId={null} slug={slug} id={id} />
        ))}
        {matchRoute("/post/:slug", ({ slug }) => (
          <ArticlePage roomId={null} slug={slug} />
        ))}
        {matchRoute("/work/:id", ({ id }) => (
          <WorkPage workId={id} />
        ))}
        {renderRoute(
          "/visual-abstracts",
          () => (
            <VisualAbstractLibraryPage />
          ),
          {
            exact: true,
          }
        )}
        {renderRoute(
          "/visual-abstracts/new",
          () => (
            <VisualAbstractCreatePage />
          ),
          { exact: true }
        )}
        {matchRoute(
          "/visual-abstract/:id/preview",
          ({ id }) => (
            <VisualAbstractPreviewPage id={id} />
          ),
          { exact: true }
        )}
        {matchRoute("/visual-abstract/report/:id", ({ id }) => (
          <VisualAbstractReportPage id={id} />
        ))}
        {matchRoute("/journal/:uid", ({ uid }) => (
          <JournalPage uid={uid} />
        ))}
        {matchRoute("/author/:id", ({ id }) => (
          <AuthorPage id={id} />
        ))}
        {matchRoute("/playlist/public/:id", ({ id }) => (
          <PlaylistPage roomId={null} id={id} />
        ))}
        {matchRoute(
          "/company/:id",
          ({ id }) => (
            <CompanyPage id={id} />
          ),
          { exact: true }
        )}
        {matchRoute("/company/:companyId/playlists", ({ companyId }) => (
          <PlaylistsListPage roomId={null} companyId={companyId} />
        ))}

        {/* On desktop web, `/signup` is the front door. Elsewhere there is
          no signup PAGE: the door is the `/welcome` sheet. The path stays
          alive for the inbound links that point at it and opens the right
          one — the router owns this transition, which is what the page it
          replaces used to do for itself on mount. Only the EXACT path
          redirects: its siblings (`/signup/onboarding`,
          `/signup/confirm-email`) are reached from emails and from the gate
          itself. */}
        {renderRoute(
          "/signup",
          () =>
            isDesktopBrowserLayout() ? (
              <DesktopAuthPage door="signup" />
            ) : (
              // The campaign rides through the redirect. Mixpanel reads
              // first-touch attribution off the URL of the first page that
              // initialises it, and that is the DESTINATION here, never
              // `/signup` — so a redirect that dropped the query reported every
              // campaign visit as direct. `campaignSearch` carries `utm_*` only,
              // deliberately: forwarding the whole query would take anything a
              // future link put on `/signup` into the autocapturing shell.
              <Redirect
                to={{
                  pathname: "/welcome",
                  search: campaignSearch(location.search),
                  state: { opensSignupSheet: true },
                }}
              />
            ),
          { exact: true }
        )}
        {renderRoute("/signup/onboarding", () => (
          <SignupOnboardingRoute
            isLoading={isLoading}
            isAuthenticated={isAuthenticated}
          />
        ))}
        {renderRoute("/signup/qrcode", () => (
          <SignupQRCodePage />
        ))}
        {renderRoute("/signup/confirm-email", () => (
          <ConfirmEmailPage />
        ))}

        {matchRoute("/invite/:inviteCode", ({ inviteCode }) => (
          <InviteLinkPage inviteCode={inviteCode} />
        ))}
        {matchRoute("/oauth/callback/:roomId", ({ roomId }) => (
          <RoomPreviewCallbackPage roomId={roomId} />
        ))}

        {renderRoute(
          "/login",
          () =>
            isDesktopBrowserLayout() ? (
              <DesktopAuthPage door="login" />
            ) : (
              <LoginPage />
            ),
          { exact: true }
        )}

        {/* The desktop profile links every edit into the settings workspace,
            so it is up exactly where that workspace is. */}
        {renderRoute(
          "/profile",
          () =>
            isDesktopLayout() ? (
              <DesktopProfilePage subject={{ kind: "me" }} />
            ) : (
              <ProfilePage isMe />
            ),
          { exact: true }
        )}

        {matchRoute(
          "/profile/user/:id",
          ({ id }) =>
            isDesktopLayout() ? (
              <DesktopProfilePage subject={{ kind: "member", id }} />
            ) : (
              <ProfilePage isMe={false} id={id} />
            ),
          { exact: true }
        )}

        {renderRoute(
          "/profile/notifications",
          () => (
            <NotificationCenter />
          ),
          {
            exact: true,
          }
        )}
        {matchRoute("/profile/notifications/:id", ({ id }) => (
          <NotificationRouter id={id} />
        ))}

        {renderRoute(
          "/profile/journals",
          () => (
            <ProfileJournals isMe />
          ),
          {
            exact: true,
          }
        )}
        {matchRoute(
          "/profile/user/:id/journals",
          ({ id }) => (
            <ProfileJournals isMe={false} id={id} />
          ),
          { exact: true }
        )}

        {renderRoute(
          "/profile/playlist",
          () =>
            isDesktopLayout() ? (
              <DesktopProfileCollectionsPage subject={{ kind: "me" }} />
            ) : (
              <ProfilePlaylistsListPage source={{ kind: "created" }} />
            ),
          {
            exact: true,
          }
        )}
        {renderRoute(
          "/profile/shared-playlist",
          () => (
            <ProfilePlaylistsListPage source={{ kind: "shared" }} />
          ),
          { exact: true }
        )}
        {renderRoute(
          "/profile/bookmarked-playlists",
          () => (
            <ProfileBookmarkedPlaylistsListPage userId={null} />
          ),
          { exact: true }
        )}
        {matchRoute(
          "/profile/user/:id/playlist",
          ({ id }) =>
            isDesktopLayout() ? (
              <DesktopProfileCollectionsPage subject={{ kind: "member", id }} />
            ) : (
              <ProfilePlaylistsListPage source={{ kind: "user", userId: id }} />
            ),
          { exact: true }
        )}
        {matchRoute(
          "/profile/user/:id/bookmarked-playlists",
          ({ id }) => (
            <ProfileBookmarkedPlaylistsListPage userId={id} />
          ),
          { exact: true }
        )}

        {renderRoute(
          "/profile/playlist/create",
          () => (
            <CreatePlaylist />
          ),
          {
            exact: true,
          }
        )}
        {matchRoute("/profile/playlist/:playlistId", ({ playlistId }) => (
          <ProfilePlaylistPage playlistId={playlistId} />
        ))}

        {matchRoute(
          "/profile/user/:id/playlist/:playlistId",
          ({ id, playlistId }) => (
            <ProfilePlaylistPage userId={id} playlistId={playlistId} />
          ),
          { exact: true }
        )}

        {renderRoute("/profile/game/activity", () => (
          <ProfileGamificationPage isMe />
        ))}
        {matchRoute("/user/game/activity/:id", ({ id }) => (
          <ProfileGamificationPage isMe={false} id={id} />
        ))}

        {/* Same logged-out keying as the main guard: Firebase session, not the
          /users/me fetch — an API hiccup must not bounce /settings to /login. */}
        {!isAuthenticated && !isLoading && (
          <Route path="/settings">
            <Redirect
              to={{ pathname: "/login", state: { fromAuthGuard: true } }}
            />
          </Route>
        )}
        {/* On desktop web, the settings are a modal over the app, and every
            address under the prefix opens it, including the ones the previous
            desktop pages published, which live in emails and push payloads.
            The routes below serve mobile, where the settings are the app-wide
            sheet. */}
        {isDesktopLayout() ? (
          <Route path="/settings">
            <DesktopSettingsRoute home={homeRedirect} />
          </Route>
        ) : null}
        {renderRoute(
          "/settings",
          () => (
            <SettingsPage />
          ),
          { exact: true }
        )}
        {renderRoute("/settings/notifications", () => (
          <SettingsNotificationsPage />
        ))}
        {renderRoute("/settings/partner-sharing", () => (
          <SettingsPartnerSharingPage />
        ))}
        {renderRoute("/settings/partners", () => (
          <SettingsPartnersPage />
        ))}
        {renderRoute("/settings/request/certification", () => (
          <RequestCertificationPage />
        ))}
        {renderRoute(
          "/settings/edit/profile",
          () => (
            <EditProfilePage />
          ),
          {
            exact: true,
          }
        )}
        {renderRoute("/settings/edit/profile/avatar", () => (
          <EditAvatarPage />
        ))}
        {renderRoute("/settings/edit/profile/preferredContent", () => (
          <EditPreferredContentPage />
        ))}
        {renderRoute("/settings/edit/language", () => (
          <SettingsEditLanguagePage />
        ))}
        {renderRoute("/settings/account", () => (
          <SettingsEditAccountPage />
        ))}

        {renderRoute("/policy", () => (
          <PolicyPage />
        ))}
        {renderRoute("/terms", () => (
          <TermsPage />
        ))}

        {renderRoute(
          "/search",
          () => (
            <SearchPage roomId={null} />
          ),
          {
            exact: true,
          }
        )}

        {renderRoute("/gpt/chat/new", () =>
          // A logged-out visitor asks before signing up, or before installing
          // the app on mobile web (`useAnonymousGptFrontDoor`).
          anonymousGptFrontDoor.kind === "open" ? (
            <GPTAnonymousChatPage offer={anonymousGptFrontDoor.offer} />
          ) : (
            // Keyed on the sidebar's "New conversation" (see the store).
            <GPTNewChatPage key={newChatGeneration} roomId={null} />
          )
        )}
        {matchRoute("/gpt/chat/conversation/:id", ({ id }) => (
          <GPTConversationPage id={id} roomId={null} />
        ))}
        {/* Desktop only: on mobile the history is the JuisciGPT drawer. */}
        {isDesktopShell()
          ? renderRoute("/gpt/history", () => <DesktopGPTHistoryPage />, {
              exact: true,
            })
          : null}
        {matchRoute("/gpt/shared/:token", ({ token }) => (
          <GPTSharedConversationPage token={token} />
        ))}
        {/* The CTA of an activation email: the reader's copy of a question and its answer. */}
        {matchRoute("/gpt/pair/:pairId/:language", ({ pairId, language }) => (
          <ActivationPairPage pairId={pairId} language={language} />
        ))}
        {isAssistantDebug
          ? renderRoute(
              "/gpt/playground",
              () => (
                <Suspense fallback={<Loader expanded />}>
                  <GenuiPlaygroundPage />
                </Suspense>
              ),
              { exact: true }
            )
          : null}

        {matchRoute(
          "/infographic/:slug",
          ({ slug }) => (
            <InfographicPage roomId={null} slug={slug} />
          ),
          { exact: true }
        )}

        {renderRoute(
          "/rooms",
          () => (
            <RoomsCenterPage />
          ),
          { exact: true }
        )}

        {renderRoute("/room", () => (
          <RoomRoutes />
        ))}

        <Route>
          <Error404Page />
        </Route>
      </Switch>
    </>
  );
}
